/* CYME CUSTOM: resilient Klaviyo embed controller.
   One cached asset owns every locale-routed signup wrapper. Mutation observers
   replace the former repeated inline scripts and polling loop, while preserving
   native fallback forms when Klaviyo is blocked or slow. */
(() => {
  if (window.__cymeKlSignup) {
    return;
  }

  window.__cymeKlSignup = true;

  const states = new WeakMap();
  const selector = '[data-cyme-kl-signup]';

  const reconcile = (wrapper, final = false) => {
    if (!wrapper.isConnected) {
      return;
    }

    const embed = wrapper.querySelector('.cyme-kl-signup__embed');
    const fallback = wrapper.querySelector('.cyme-kl-signup__fallback');

    if (wrapper.dataset.cymeEngaged) {
      return;
    }

    // Native form submissions return their success/error banner inside the
    // fallback. That state belongs to the visitor even if Klaviyo is available.
    if (fallback?.querySelector('.banner')) {
      fallback.hidden = false;
      wrapper.dataset.cymeEngaged = '1';
      wrapper.classList.remove('is-live');
      wrapper.classList.add('is-fallback');
      return;
    }

    if (embed?.childElementCount > 0) {
      if (fallback) {
        fallback.hidden = true;
      }

      wrapper.classList.remove('is-fallback');
      wrapper.classList.add('is-live');
      return;
    }

    if (final) {
      if (fallback) {
        fallback.hidden = false;
      }

      wrapper.classList.remove('is-live');
      wrapper.classList.add(fallback ? 'is-fallback' : 'is-live');
    }
  };

  const bind = (wrapper) => {
    if (states.has(wrapper)) {
      reconcile(wrapper);
      return;
    }

    const embed = wrapper.querySelector('.cyme-kl-signup__embed');
    const fallback = wrapper.querySelector('.cyme-kl-signup__fallback');
    const state = { observer: null, fallbackTimer: 0, legacyTimer: 0 };

    if (fallback) {
      const markEngaged = () => {
        wrapper.dataset.cymeEngaged = '1';
      };

      fallback.addEventListener('focusin', markEngaged);
      fallback.addEventListener('input', markEngaged);
    }

    if (embed && 'MutationObserver' in window) {
      state.observer = new MutationObserver(() => reconcile(wrapper));
      state.observer.observe(embed, { childList: true });
    } else if (embed) {
      // MutationObserver predates every supported Prestige browser. These two
      // one-shot checks retain a graceful path for unusually old webviews.
      state.legacyTimer = window.setTimeout(() => reconcile(wrapper), 7000);
    }

    state.fallbackTimer = window.setTimeout(() => reconcile(wrapper, true), 3000);
    states.set(wrapper, state);
    reconcile(wrapper);
  };

  const wrappersWithin = (root) => {
    const wrappers = [];

    if (root instanceof Element && root.matches(selector)) {
      wrappers.push(root);
    }

    root.querySelectorAll?.(selector).forEach((wrapper) => wrappers.push(wrapper));
    return wrappers;
  };

  const init = (root = document) => {
    wrappersWithin(root).forEach(bind);
  };

  const disconnect = (root) => {
    wrappersWithin(root).forEach((wrapper) => {
      const state = states.get(wrapper);

      if (!state) {
        return;
      }

      state.observer?.disconnect();
      window.clearTimeout(state.fallbackTimer);
      window.clearTimeout(state.legacyTimer);
      states.delete(wrapper);
    });
  };

  init();
  window.addEventListener('klaviyoForms', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => disconnect(event.target));
})();
